<template>
    <div class="weekday-time-selector">
        <div class="selector-header">
            <h4>Настройки времени по дням недели</h4>
        </div>

        <div class="weekdays-container">
            <div v-for="(dayName, dayNumber) in availableDaysOfWeek" :key="dayNumber"
                class="weekday-section has-settings">
                <div class="weekday-header">
                    <div class="weekday-name">
                        <span class="day-label">
                            {{ dayName }}
                        </span>
                        <span class="day-multiplier">
                            x{{ getDayMultiplier(dayNumber) }}
                        </span>
                    </div>

                    <!-- Визуализация часов в заголовке -->
                    <div class="hour-grid-container">
                        <!-- Подсказка с часами -->
                        <div class="hour-labels">
                            <div v-for="hour in 24" :key="`label-${hour}`" class="hour-label">
                                {{ (hour - 1).toString().padStart(2, '0') }}
                            </div>
                        </div>
                        <!-- Чекбоксы -->
                        <div class="hour-grid">
                            <div v-for="hour in 24" :key="hour" class="hour-checkbox" :class="{
                                active: isHourActiveForDay(dayNumber, hour)
                            }" @click="toggleHourForDay(dayNumber, hour)"
                                :title="`${formatHourRange(hour)} - ${isHourActiveForDay(dayNumber, hour) ? 'активен' : 'неактивен'}`">
                                <div class="checkbox-square">
                                    <div class="square-fill"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="day-summary">
                        <span class="total-hours">
                            {{ getTotalHoursForDay(dayNumber) }}ч × {{ getDayMultiplier(dayNumber) }} = {{
                                getTotalHoursForDay(dayNumber) * getDayMultiplier(dayNumber) }}ч
                        </span>
                        <span class="total-cost">{{ formatPrice(getTotalCostForDay(dayNumber)) }}</span>
                    </div>
                </div>

            </div>
        </div>

        <div class="global-summary">
            <div class="summary-row">
                <span class="summary-label">Всего дней:</span>
                <span class="summary-value">{{ getActiveDaysCount() }}</span>
            </div>
            <div class="summary-row">
                <span class="summary-label">Всего часов:</span>
                <span class="summary-value">{{ getTotalHours() }}ч</span>
                <span class="summary-separator">×</span>
                <span class="summary-label">стоимость за час</span>
                <span class="summary-value">{{ formatPrice(hourlyPrice) }}</span>
                <span class="summary-separator">=</span>
                <span class="summary-label">Общая стоимость:</span>
                <span class="summary-value total-cost">{{ formatPrice(getTotalCost()) }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import HourCheckboxes from './HourCheckboxes.vue';

export default {
    name: 'WeekdayTimeSelector',
    components: {
        HourCheckboxes
    },
    props: {
        timeSettings: {
            type: Array,
            default: () => []
        },
        hourlyPrice: {
            type: Number,
            default: 0
        },
        activePeriods: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:timeSettings'],
    data() {
        return {
            daysOfWeek: {
                1: 'Понедельник',
                2: 'Вторник',
                3: 'Среда',
                4: 'Четверг',
                5: 'Пятница',
                6: 'Суббота',
                7: 'Воскресенье'
            },
            availableHours: Array.from({ length: 24 }, (_, i) => i), // 0-23
            localTimeSettings: [],
            isInitializing: false // Флаг для предотвращения рекурсивных обновлений
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.isInitializing = true;
            if (this.timeSettings && this.timeSettings.length > 0) {
                this.localTimeSettings = [...this.timeSettings];
                // Проверяем, нужно ли добавить настройки для новых дней
                this.ensureAllDaysInitialized(true);
            } else {
                // Инициализируем все доступные дни с интервалом 00:00-24:00 (24 часа)
                this.initializeAllDays(true);
            }
            this.isInitializing = false;
        });
    },
    watch: {
        timeSettings: {
            handler(newSettings) {
                // Пропускаем обновление во время инициализации
                if (this.isInitializing) return;

                // Сравниваем с текущими настройками, чтобы избежать ненужных обновлений
                const settingsChanged = JSON.stringify(newSettings || []) !== JSON.stringify(this.localTimeSettings);
                if (!settingsChanged) return;

                if (newSettings && newSettings.length > 0) {
                    this.isInitializing = true;
                    this.localTimeSettings = [...newSettings];
                    // Проверяем, нужно ли добавить настройки для новых дней
                    this.ensureAllDaysInitialized(false);
                    this.isInitializing = false;
                } else if (!this.localTimeSettings || this.localTimeSettings.length === 0) {
                    // Если пришел пустой массив и у нас тоже пусто, инициализируем все дни
                    this.isInitializing = true;
                    this.initializeAllDays(false);
                    this.isInitializing = false;
                }
            },
            deep: true
        },
        activePeriods: {
            handler() {
                // Пропускаем обновление во время инициализации
                if (this.isInitializing) return;

                // При изменении периодов активности проверяем и инициализируем новые дни
                this.$nextTick(() => {
                    this.isInitializing = true;
                    this.ensureAllDaysInitialized(false);
                    this.isInitializing = false;
                });
            },
            deep: true
        },
        availableDaysOfWeek: {
            handler(newAvailableDays) {
                // Пропускаем обновление во время инициализации
                if (this.isInitializing) return;

                // Удаляем настройки времени для дней, которые больше не доступны
                const availableDayNumbers = Object.keys(newAvailableDays).map(Number);
                const filteredSettings = this.localTimeSettings.filter(ts =>
                    availableDayNumbers.includes(ts.day)
                );

                // Проверяем, были ли изменения
                if (filteredSettings.length !== this.localTimeSettings.length) {
                    this.isInitializing = true;
                    this.localTimeSettings = filteredSettings;
                    // Добавляем настройки для новых дней, которых еще нет
                    this.ensureAllDaysInitialized(false);
                    this.isInitializing = false;
                } else {
                    // Просто проверяем, нет ли новых дней для добавления
                    this.isInitializing = true;
                    this.ensureAllDaysInitialized(false);
                    this.isInitializing = false;
                }
            },
            deep: true
        }
    },
    computed: {
        availableDaysOfWeek() {
            // Получаем дни недели, которые присутствуют в периодах активности
            if (!this.activePeriods || this.activePeriods.length === 0) {
                return this.daysOfWeek; // Если периоды не заданы, показываем все дни
            }

            const availableDays = {};

            // Проходим по каждому периоду
            this.activePeriods.forEach(period => {
                if (!period.start || !period.end) return;

                const startDate = new Date(period.start);
                const endDate = new Date(period.end);

                // Проходим по каждому дню в периоде
                const currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Воскресенье = 7

                    if (this.daysOfWeek[dayOfWeek]) {
                        availableDays[dayOfWeek] = this.daysOfWeek[dayOfWeek];
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });

            return availableDays;
        },
        dayMultipliers() {
            // Подсчитываем количество вхождений каждого дня недели во всех периодах активности
            const multipliers = {};

            if (!this.activePeriods || this.activePeriods.length === 0) {
                // Если периоды не заданы, возвращаем все дни с множителем 1
                for (let day = 1; day <= 7; day++) {
                    multipliers[day] = 1;
                }
                return multipliers;
            }

            // Инициализируем все дни с 0
            for (let day = 1; day <= 7; day++) {
                multipliers[day] = 0;
            }

            // Проходим по каждому периоду и считаем дни
            this.activePeriods.forEach(period => {
                if (!period.start || !period.end) return;

                const startDate = new Date(period.start);
                const endDate = new Date(period.end);

                // Проходим по каждому дню в периоде и считаем
                const currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Воскресенье = 7
                    if (multipliers[dayOfWeek] !== undefined) {
                        multipliers[dayOfWeek]++;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            });

            return multipliers;
        }
    },
    methods: {
        hasTimeSettingsForDay(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.localTimeSettings.some(ts => ts.day === dayNum);
        },

        getTimeSettingsForDay(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.localTimeSettings.filter(ts => ts.day === dayNum);
        },

        initializeAllDays(silent = false) {
            // Инициализируем все доступные дни с интервалом 00:00-24:00 (24 часа)
            const availableDayNumbers = Object.keys(this.availableDaysOfWeek).map(Number);
            availableDayNumbers.forEach(dayNumber => {
                if (!this.hasTimeSettingsForDay(dayNumber)) {
                    this.localTimeSettings.push({
                        day: dayNumber,
                        startTime: 0, // 00:00
                        endTime: 24 * 60 * 60 * 1000 // 24:00 (24 часа)
                    });
                }
            });
            if (!silent) {
                this.emitUpdate();
            }
        },
        ensureAllDaysInitialized(silent = false) {
            // Проверяем и добавляем настройки для дней, которых еще нет
            const availableDayNumbers = Object.keys(this.availableDaysOfWeek).map(Number);
            let hasChanges = false;

            availableDayNumbers.forEach(dayNumber => {
                if (!this.hasTimeSettingsForDay(dayNumber)) {
                    this.localTimeSettings.push({
                        day: dayNumber,
                        startTime: 0, // 00:00
                        endTime: 24 * 60 * 60 * 1000 // 24:00 (24 часа)
                    });
                    hasChanges = true;
                }
            });

            if (hasChanges && !silent) {
                this.emitUpdate();
            }
        },
        isHourActiveForDay(dayNumber, hour) {
            // Проверяем, активен ли час для конкретного дня
            const dayNum = Number(dayNumber);
            const daySettings = this.getTimeSettingsForDay(dayNum);

            if (daySettings.length === 0) {
                return false;
            }

            const hourMs = (hour - 1) * 60 * 60 * 1000; // Конвертируем час в миллисекунды

            return daySettings.some(ts => {
                return hourMs >= ts.startTime && hourMs < ts.endTime;
            });
        },
        toggleHourForDay(dayNumber, hour) {
            // Переключаем час для конкретного дня
            const dayNum = Number(dayNumber);
            const hourMs = (hour - 1) * 60 * 60 * 1000;
            const daySettings = this.getTimeSettingsForDay(dayNum);

            // Находим интервал, который содержит этот час
            const containingInterval = daySettings.find(ts =>
                hourMs >= ts.startTime && hourMs < ts.endTime
            );

            if (containingInterval) {
                // Проверяем, не является ли это последним активным часом для дня
                const totalActiveHours = this.getTotalHoursForDay(dayNum);
                if (totalActiveHours <= 1) {
                    // Не даем удалить последний активный час
                    return;
                }
                // Если час активен, деактивируем его
                this.deactivateHourInInterval(dayNum, hourMs);
            } else {
                // Если час неактивен, активируем его
                this.activateHourInInterval(dayNum, hourMs);
            }

            this.emitUpdate();
        },
        activateHourInInterval(dayNumber, hourMs) {
            // Активируем час, создавая или расширяя интервал
            const dayNum = Number(dayNumber);
            const daySettings = this.getTimeSettingsForDay(dayNum);

            // Удаляем старые настройки для этого дня
            this.localTimeSettings = this.localTimeSettings.filter(ts => ts.day !== dayNum);

            // Проверяем, можно ли расширить существующий интервал или объединить два интервала
            let merged = false;
            const newIntervals = [];

            // Сортируем интервалы по времени начала
            const sortedSettings = [...daySettings].sort((a, b) => a.startTime - b.startTime);

            // Сначала проверяем, можно ли объединить два соседних интервала
            for (let i = 0; i < sortedSettings.length; i++) {
                const interval = sortedSettings[i];
                const nextInterval = sortedSettings[i + 1];

                // Проверяем, находится ли час между концом текущего и началом следующего интервала
                if (nextInterval && hourMs === interval.endTime && hourMs === nextInterval.startTime - 60 * 60 * 1000) {
                    // Час находится между двумя интервалами - объединяем их в один
                    newIntervals.push({
                        day: dayNum,
                        startTime: interval.startTime,
                        endTime: nextInterval.endTime
                    });
                    i++; // Пропускаем следующий интервал, так как он уже объединен
                    merged = true;
                } else if (hourMs === interval.startTime - 60 * 60 * 1000) {
                    // Час идет прямо перед началом интервала - расширяем начало
                    newIntervals.push({
                        day: dayNum,
                        startTime: hourMs,
                        endTime: interval.endTime
                    });
                    merged = true;
                } else if (hourMs === interval.endTime) {
                    // Час идет прямо после конца интервала - расширяем конец
                    newIntervals.push({
                        day: dayNum,
                        startTime: interval.startTime,
                        endTime: hourMs + 60 * 60 * 1000
                    });
                    merged = true;
                } else {
                    // Не можем объединить - добавляем интервал как есть
                    newIntervals.push(interval);
                }
            }

            // Если не удалось объединить с существующими интервалами, создаем новый
            if (!merged) {
                newIntervals.push({
                    day: dayNum,
                    startTime: hourMs,
                    endTime: hourMs + 60 * 60 * 1000
                });
            }

            // Добавляем все интервалы обратно
            this.localTimeSettings.push(...newIntervals);
        },
        deactivateHourInInterval(dayNumber, hourMs) {
            // Деактивируем час, удаляя или сокращая интервал
            const dayNum = Number(dayNumber);
            const daySettings = this.getTimeSettingsForDay(dayNum);

            // Удаляем старые настройки для этого дня
            this.localTimeSettings = this.localTimeSettings.filter(ts => ts.day !== dayNum);

            // Создаем новые интервалы, исключая деактивированный час
            daySettings.forEach(ts => {
                if (hourMs >= ts.startTime && hourMs < ts.endTime) {
                    // Час находится в этом интервале
                    if (hourMs === ts.startTime) {
                        // Час в начале интервала - сдвигаем начало
                        if (ts.endTime > ts.startTime + 60 * 60 * 1000) {
                            this.localTimeSettings.push({
                                day: dayNum,
                                startTime: ts.startTime + 60 * 60 * 1000,
                                endTime: ts.endTime
                            });
                        }
                        // Если интервал состоял из одного часа, он просто удаляется (не добавляется)
                    } else if (hourMs === ts.endTime - 60 * 60 * 1000) {
                        // Час в конце интервала - сдвигаем конец
                        if (ts.startTime < ts.endTime - 60 * 60 * 1000) {
                            this.localTimeSettings.push({
                                day: dayNum,
                                startTime: ts.startTime,
                                endTime: ts.endTime - 60 * 60 * 1000
                            });
                        }
                        // Если интервал состоял из одного часа, он просто удаляется (не добавляется)
                    } else {
                        // Час в середине интервала - разбиваем на два
                        this.localTimeSettings.push({
                            day: dayNum,
                            startTime: ts.startTime,
                            endTime: hourMs
                        });
                        this.localTimeSettings.push({
                            day: dayNum,
                            startTime: hourMs + 60 * 60 * 1000,
                            endTime: ts.endTime
                        });
                    }
                } else {
                    // Час не в этом интервале - оставляем как есть
                    this.localTimeSettings.push(ts);
                }
            });
        },

        getHourFromTime(timeMs) {
            return Math.floor(timeMs / (60 * 60 * 1000));
        },

        getTotalHoursForDay(dayNumber) {
            const daySettings = this.getTimeSettingsForDay(dayNumber);
            let totalHours = 0;

            daySettings.forEach(ts => {
                const startHour = this.getHourFromTime(ts.startTime);
                const endHour = this.getHourFromTime(ts.endTime);
                totalHours += endHour - startHour;
            });

            return totalHours;
        },

        getTotalCostForDay(dayNumber) {
            const dayNum = Number(dayNumber);
            const baseCost = this.getTotalHoursForDay(dayNum) * this.hourlyPrice;
            const multiplier = this.getDayMultiplier(dayNum);
            return baseCost * multiplier;
        },
        getDayMultiplier(dayNumber) {
            const dayNum = Number(dayNumber);
            return this.dayMultipliers[dayNum] || 1;
        },

        getActiveDaysCount() {
            // Считаем суммарное количество вхождений всех активных дней недели
            const activeDayNumbers = new Set(this.localTimeSettings.map(ts => ts.day));
            let totalCount = 0;

            activeDayNumbers.forEach(dayNum => {
                const multiplier = this.getDayMultiplier(dayNum);
                totalCount += multiplier;
            });

            return totalCount;
        },

        getTotalHours() {
            // Считаем общее количество часов с учетом множителей дней недели
            let totalHours = 0;
            const activeDayNumbers = new Set(this.localTimeSettings.map(ts => ts.day));

            activeDayNumbers.forEach(dayNum => {
                const dayHours = this.getTotalHoursForDay(dayNum);
                const multiplier = this.getDayMultiplier(dayNum);
                totalHours += dayHours * multiplier;
            });

            return totalHours;
        },

        getTotalCost() {
            // Считаем общую стоимость с учетом множителей для каждого дня
            let totalCost = 0;
            const activeDayNumbers = new Set(this.localTimeSettings.map(ts => ts.day));

            activeDayNumbers.forEach(dayNum => {
                const dayHours = this.getTotalHoursForDay(dayNum);
                const dayCost = dayHours * this.hourlyPrice;
                const multiplier = this.getDayMultiplier(dayNum);
                totalCost += dayCost * multiplier;
            });

            return totalCost;
        },

        formatPrice(amount) {
            const num = Number(amount || 0);
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            }).format(num);
        },
        formatHourRange(hour) {
            const startHour = hour - 1;
            const endHour = hour;
            return `${startHour.toString().padStart(2, '0')}:00 - ${endHour.toString().padStart(2, '0')}:00`;
        },


        emitUpdate() {
            this.$emit('update:timeSettings', [...this.localTimeSettings]);
        }
    }
};
</script>

<style scoped>
.weekday-time-selector {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.selector-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 12px 16px;
    border-bottom: 1px solid #e2e8f0;
}

.selector-header h4 {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
}

.selector-description {
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
}

.weekdays-container {
    padding: 0;
}

.weekday-section {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.2s ease;
}

.weekday-section:last-child {
    border-bottom: none;
}

.weekday-section.has-settings {
    background: #f8fafc;
}

.weekday-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    gap: 12px;
}

.weekday-name {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 120px;
}

.day-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    user-select: none;
}

.day-multiplier {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    margin-left: 6px;
    user-select: none;
}

.hour-grid-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    margin: 0 12px;
}

.hour-labels {
    display: flex;
    gap: 2px;
    margin-bottom: 4px;
}

.hour-label {
    width: 17px;
    text-align: center;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 500;
}

.hour-grid {
    display: flex;
    gap: 2px;
    justify-content: center;
}

.hour-checkbox {
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    width: 17px !important;
    height: 17px !important;
}

.hour-checkbox:hover {
    transform: scale(1.1);
}

.checkbox-square {
    width: 100% !important;
    height: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 3px !important;
    position: relative !important;
    transition: all 0.2s ease !important;
}

.square-fill {
    width: 0% !important;
    height: 0% !important;
    background: #10b981 !important;
    border-radius: 2px !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    transition: all 0.2s ease !important;
}

.hour-checkbox.active .checkbox-square {
    border: none !important;
    background: transparent !important;
}

.hour-checkbox.active .square-fill {
    width: 100% !important;
    height: 100% !important;
}

.day-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    min-width: 80px;
    justify-content: flex-end;
}

.total-hours {
    color: #475569;
    font-weight: 600;
}

.total-cost {
    color: #059669;
    font-weight: 700;
}

.global-summary {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    padding: 16px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.summary-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.summary-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.summary-value {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

.summary-value.total-cost {
    color: #059669;
}

.summary-separator {
    font-size: 14px;
    color: #94a3b8;
    font-weight: 500;
}

/* Адаптивность */
@media (max-width: 768px) {
    .selector-header {
        padding: 10px 12px;
    }

    .weekday-header {
        padding: 8px 12px;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .time-settings {
        padding: 0 12px 10px 12px;
    }

    .interval-controls {
        flex-wrap: wrap;
        gap: 4px;
    }

    .global-summary {
        padding: 12px;
        gap: 10px;
    }

    .summary-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
}
</style>
